/**
 * Phase F-finale-7 Slice 7b.4b — IA seat-management Seats API client.
 *
 * Wraps the real-auth seating Seat surface (eduviaos-backend
 * `app/domains/seating/routes.py`, all `manage_zones`-guarded):
 *
 *   GET    /api/seats            (list, zone/branch/active filters + paging)
 *   POST   /api/seats            (create)
 *   PATCH  /api/seats/{seat_id}  (update)
 *
 * Tenant + actor are resolved BE-side from the JWT `AuthContext`; none of
 * those are client-trusted. Reads + writes both require `MANAGE_ZONES`
 * (enforced server-side via the seating `_ADMIN_GUARD`; the FE additionally
 * gates write affordances via `useCanEditSeating`, the same hook the Zones
 * (7b.4a) surface uses).
 *
 * The wire shapes mirror `app/domains/seating/schemas.py` (`SeatResponse`,
 * `CreateSeatRequest`, `UpdateSeatRequest`, `SeatListResponse`) exactly —
 * any field-shape change on the BE must land here too (Iron Rule #1/#11).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiError } from "@eduviaos/shared";
import { api } from "@/lib/api";

/** Mirror of `SeatResponse`. A seat belongs to exactly one zone and, unlike
 *  zones, has no slug/capacity — its identity is `seat_number` within its
 *  `zone_id`. `zone_id` is immutable post-create (no PATCH field for it). */
export interface Seat {
  id: string;
  tenant_id: string;
  branch_id: string | null;
  zone_id: string;
  seat_number: string;
  seat_type: string;
  is_active: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface SeatListResponse {
  items: Seat[];
  total: number;
}

/** Mirror of `CreateSeatRequest`. `seat_type` defaults to "standard" BE-side. */
export interface CreateSeatInput {
  zone_id: string;
  seat_number: string;
  seat_type?: string;
  branch_id?: string | null;
  metadata?: Record<string, unknown>;
}

/** Mirror of `UpdateSeatRequest` — every field optional (PATCH semantics).
 *  Note there is no `zone_id`: a seat cannot be moved between zones. */
export interface UpdateSeatInput {
  seat_number?: string;
  seat_type?: string;
  is_active?: boolean;
  metadata?: Record<string, unknown>;
}

export interface SeatListFilters {
  zone_id?: string;
  branch_id?: string;
  is_active?: boolean;
  limit?: number;
  offset?: number;
}

// ── React Query hooks ─────────────────────────────────────────────────

export const seatKeys = {
  all: ["ia", "seating", "seats"] as const,
  list: (filters?: SeatListFilters) => [...seatKeys.all, "list", filters ?? {}] as const,
};

function buildSeatsQuery(filters?: SeatListFilters): string {
  const search = new URLSearchParams();
  if (!filters) return "";
  if (filters.zone_id) search.set("zone_id", filters.zone_id);
  if (filters.branch_id) search.set("branch_id", filters.branch_id);
  if (filters.is_active !== undefined) search.set("is_active", String(filters.is_active));
  if (filters.limit !== undefined) search.set("limit", String(filters.limit));
  if (filters.offset !== undefined) search.set("offset", String(filters.offset));
  return search.toString();
}

export function useSeats(filters?: SeatListFilters) {
  const qs = buildSeatsQuery(filters);
  return useQuery<SeatListResponse>({
    queryKey: seatKeys.list(filters),
    queryFn: () => api.get<SeatListResponse>(`/api/seats${qs ? `?${qs}` : ""}`),
    staleTime: 15_000,
  });
}

export function useCreateSeat() {
  const qc = useQueryClient();
  return useMutation<Seat, ApiError, CreateSeatInput>({
    mutationKey: ["ia", "seating", "seats", "create"],
    mutationFn: (body) => api.post<Seat>("/api/seats", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: seatKeys.all });
    },
  });
}

export interface UpdateSeatVars {
  id: string;
  body: UpdateSeatInput;
}

export function useUpdateSeat() {
  const qc = useQueryClient();
  return useMutation<Seat, ApiError, UpdateSeatVars>({
    mutationKey: ["ia", "seating", "seats", "update"],
    mutationFn: ({ id, body }) => api.patch<Seat>(`/api/seats/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: seatKeys.all });
    },
  });
}
